import React, { useState, useEffect } from 'react';
import { useLMS } from '../../context/LMSContext';
import { supabase } from '../../lib/supabase';

const DocumentsView = () => {
  const { currentUser, selectedCohortId, cohorts } = useLMS();

  const isAdmin = currentUser?.role === 'admin';
  const isProfessor = currentUser?.role === 'professor';
  const isStaff = isAdmin || isProfessor;

  // Tab: 'student' = student-facing docs, 'educator' = educator-only docs (staff only)
  const [activeTab, setActiveTab] = useState(isStaff ? 'educator' : 'student');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [newDoc, setNewDoc] = useState({
    title: '',
    description: '',
    category: 'general'
  });

  // Categories for organizing documents
  const staffCategories = [
    { value: 'email-template', label: '📧 Email Template' },
    { value: 'policy', label: '📋 Policy Document' },
    { value: 'guide', label: '📖 Educator Guide' },
    { value: 'form', label: '📝 Form / Template' },
    { value: 'general', label: '📁 General' }
  ];

  const studentCategories = [
    { value: 'lecture', label: '🎓 Lecture Material' },
    { value: 'faq', label: '❓ FAQ' },
    { value: 'study-guide', label: '📚 Study Guide' },
    { value: 'resource', label: '🔗 Resource' },
    { value: 'general', label: '📁 General' }
  ];

  const categories = activeTab === 'educator' ? staffCategories : studentCategories;

  // Load documents
  useEffect(() => {
    loadDocuments();
  }, [activeTab]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('lms_documents')
        .select('*, uploader:uploaded_by(name)')
        .order('created_at', { ascending: false });

      if (activeTab === 'educator') {
        query = query.eq('audience', 'educator');
      } else {
        query = query.eq('audience', 'student');
      }

      const { data, error } = await query;
      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      console.error('Error loading documents:', err);
      setDocuments([]);
    }
    setLoading(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !newDoc.title.trim()) return;

    setUploading(true);
    setUploadError('');

    try {
      // Upload file to Supabase Storage
      const sanitizedName = selectedFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `documents/${activeTab}/${Date.now()}-${sanitizedName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('syllabus-pdfs')
        .upload(filePath, selectedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('syllabus-pdfs')
        .getPublicUrl(filePath);

      const fileUrl = urlData.publicUrl;

      // Insert document record
      const { data: docRecord, error: dbError } = await supabase
        .from('lms_documents')
        .insert({
          title: newDoc.title.trim(),
          description: newDoc.description.trim() || null,
          category: newDoc.category,
          audience: activeTab === 'educator' ? 'educator' : 'student',
          file_url: fileUrl,
          file_name: selectedFile.name,
          file_size: formatFileSize(selectedFile.size),
          file_type: selectedFile.type || 'application/octet-stream',
          storage_path: filePath,
          uploaded_by: currentUser?.id
        })
        .select('*, uploader:uploaded_by(name)')
        .single();

      if (dbError) throw dbError;

      setDocuments(prev => [docRecord, ...prev]);
      setShowUploadModal(false);
      setNewDoc({ title: '', description: '', category: 'general' });
      setSelectedFile(null);
    } catch (err) {
      console.error('Error uploading document:', err);
      setUploadError(err.message || 'Failed to upload document');
    }
    setUploading(false);
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;

    try {
      // Delete from storage
      if (doc.storage_path) {
        await supabase.storage.from('syllabus-pdfs').remove([doc.storage_path]);
      }

      // Delete from database
      const { error } = await supabase.from('lms_documents').delete().eq('id', doc.id);
      if (error) throw error;

      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Failed to delete document: ' + err.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    const kb = bytes / 1024;
    return `${kb.toFixed(0)} KB`;
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return '📄';
    if (fileType.includes('pdf')) return '📕';
    if (fileType.includes('word') || fileType.includes('docx') || fileType.includes('doc')) return '📘';
    if (fileType.includes('sheet') || fileType.includes('xlsx') || fileType.includes('csv')) return '📊';
    if (fileType.includes('image') || fileType.includes('png') || fileType.includes('jpg')) return '🖼️';
    if (fileType.includes('video')) return '🎬';
    return '📄';
  };

  const getCategoryLabel = (value) => {
    const allCats = [...staffCategories, ...studentCategories];
    return allCats.find(c => c.value === value)?.label || '📁 General';
  };

  // Group documents by category
  const groupedDocs = documents.reduce((groups, doc) => {
    const cat = doc.category || 'general';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(doc);
    return groups;
  }, {});

  return (
    <div className="documents-view" style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>📁 Documents</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: '14px' }}>
            {activeTab === 'educator' ? 'Internal documents for educators only' : 'Course documents and resources for students'}
          </p>
        </div>
        {isStaff && (
          <button
            className="btn-primary"
            onClick={() => {
              setNewDoc({ title: '', description: '', category: 'general' });
              setSelectedFile(null);
              setUploadError('');
              setShowUploadModal(true);
            }}
          >
            📤 Upload Document
          </button>
        )}
      </div>

      {/* Tab Switcher - staff sees both tabs */}
      {isStaff && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: 'var(--gray-100)', borderRadius: '10px', padding: '4px' }}>
          <button
            onClick={() => setActiveTab('educator')}
            style={{
              flex: 1,
              padding: '10px 16px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: activeTab === 'educator' ? '600' : '400',
              background: activeTab === 'educator' ? 'white' : 'transparent',
              color: activeTab === 'educator' ? 'var(--red-primary, #dc2626)' : 'var(--gray-600)',
              boxShadow: activeTab === 'educator' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            🔒 Educator Documents
          </button>
          <button
            onClick={() => setActiveTab('student')}
            style={{
              flex: 1,
              padding: '10px 16px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: activeTab === 'student' ? '600' : '400',
              background: activeTab === 'student' ? 'white' : 'transparent',
              color: activeTab === 'student' ? 'var(--red-primary, #dc2626)' : 'var(--gray-600)',
              boxShadow: activeTab === 'student' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            📚 Student Documents
          </button>
        </div>
      )}

      {/* Documents List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
          Loading documents...
        </div>
      ) : documents.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'var(--gray-50)',
          borderRadius: '12px',
          color: 'var(--gray-500)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📂</div>
          <h3 style={{ margin: '0 0 8px', color: 'var(--gray-700)' }}>No documents yet</h3>
          <p style={{ margin: 0 }}>
            {isStaff
              ? `Upload ${activeTab === 'educator' ? 'educator-only' : 'student-facing'} documents to get started.`
              : 'No documents have been shared yet. Check back later!'}
          </p>
        </div>
      ) : (
        Object.entries(groupedDocs).map(([category, docs]) => (
          <div key={category} style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
              {getCategoryLabel(category)}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {docs.map(doc => (
                <div
                  key={doc.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    background: 'white',
                    borderRadius: '10px',
                    border: '1px solid var(--gray-200)',
                    transition: 'box-shadow 0.2s'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>{getFileIcon(doc.file_type)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '500', marginBottom: '2px' }}>{doc.title}</div>
                    {doc.description && (
                      <div style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '2px' }}>{doc.description}</div>
                    )}
                    <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
                      {doc.file_name} {doc.file_size && `· ${doc.file_size}`}
                      {doc.uploader?.name && ` · Uploaded by ${doc.uploader.name}`}
                      {doc.created_at && ` · ${new Date(doc.created_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                    style={{ whiteSpace: 'nowrap', fontSize: '13px', padding: '6px 14px', textDecoration: 'none' }}
                  >
                    📥 Download
                  </a>
                  {isStaff && (
                    <button
                      onClick={() => handleDelete(doc)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        color: 'var(--gray-400)',
                        padding: '4px 8px',
                        borderRadius: '6px'
                      }}
                      title="Delete document"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Upload Document</h3>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>×</button>
            </div>

            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label>Audience</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className={activeTab === 'educator' ? 'btn-primary' : 'btn-secondary'}
                    onClick={() => setActiveTab('educator')}
                    style={{ flex: 1 }}
                  >
                    🔒 Educators Only
                  </button>
                  <button
                    type="button"
                    className={activeTab === 'student' ? 'btn-primary' : 'btn-secondary'}
                    onClick={() => setActiveTab('student')}
                    style={{ flex: 1 }}
                  >
                    📚 Students
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Title <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="text"
                  placeholder="e.g., Course FAQ, Welcome Email Template..."
                  value={newDoc.title}
                  onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  value={newDoc.category}
                  onChange={(e) => setNewDoc({ ...newDoc, category: e.target.value })}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Description (optional)</label>
                <input
                  type="text"
                  placeholder="Brief description of this document..."
                  value={newDoc.description}
                  onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>File <span style={{ color: '#dc2626' }}>*</span></label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    id="doc-upload-input"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    accept=".pdf,.doc,.docx,.txt,.rtf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.pptx,.ppt"
                    style={{ display: 'none' }}
                  />
                  {selectedFile ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'var(--gray-50)', borderRadius: '8px' }}>
                      <span>📄 {selectedFile.name}</span>
                      <span style={{ color: 'var(--gray-400)', fontSize: '12px' }}>({formatFileSize(selectedFile.size)})</span>
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="doc-upload-input" style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '24px',
                      border: '2px dashed var(--gray-300)',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}>
                      <span style={{ fontSize: '32px', marginBottom: '8px' }}>📤</span>
                      <span style={{ fontWeight: '500' }}>Click to choose file</span>
                      <span style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
                        PDF, Word, Excel, PowerPoint, Images, Text
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {uploadError && (
                <div style={{ padding: '10px', background: '#fef2f2', color: '#dc2626', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>
                  {uploadError}
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowUploadModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={uploading || !selectedFile || !newDoc.title.trim()}>
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsView;
